<template>
  <div class="final-record-viewer-wrapper">
    <!-- شريط الأدوات للتحكم في الطباعة -->
    <div class="print-toolbar">
      <el-button type="primary" @click="printPage" size="large"><el-icon><Printer /></el-icon> طباعة السجل</el-button>
      <el-button @click="$router.go(-1)" size="large">العودة</el-button>
    </div>

    <!-- عرض البيانات عند التحميل -->
    <div v-if="loading" v-loading="loading" element-loading-text="جاري تحضير السجل النهائي..." style="height: 300px;"></div>
    <div v-if="!loading && !classInfo" class="no-data">
        <el-empty description="لا توجد بيانات لعرضها. قد يكون خطأ قد حدث." />
    </div>

    <!-- المحتوى القابل للطباعة -->
    <div id="printable-content" v-if="!loading && classInfo">
      <div class="report-card-page">
        <!-- الهيدر من القالب -->
        <header v-if="template.header_content" v-html="processTemplateContent(template.header_content)" class="report-header"></header>
        
        <!-- جدول النتائج المفصل -->
        <main class="report-body">
          <table class="final-record-table">
            <thead>
              <tr>
                <th rowspan="2" style="min-width: 150px;">اسم الطالب</th>
                <th v-for="subject in subjects" :key="subject.id" :colspan="colspanCount">{{ subject.name }}</th>
                <th rowspan="2">المعدل السنوي</th>
                <th rowspan="2">حالة الترفيع</th>
              </tr>
              <tr>
                <template v-for="subject in subjects" :key="subject.id + '-sub'">
                    <th v-if="showTerm1">{{ term1?.name || 'الفصل الأول' }}</th>
                    <th v-if="showTerm2">{{ term2?.name || 'الفصل الثاني' }}</th>
                    <th v-if="showFinalYear">النهائي</th>
                </template>
              </tr>
            </thead>
            <tbody>
              <tr v-for="student in studentsData" :key="student.student_info.id">
                <td>{{ student.student_info.name }}</td>
                <template v-for="subject in subjects" :key="subject.id + '-data'">
                    <!-- --- التعديل الرئيسي هنا: تمرير term.id --- -->
                    <td v-if="showTerm1">{{ getGradeForTerm(student.term_grades, subject.id, term1?.id) }}</td>
                    <td v-if="showTerm2">{{ getGradeForTerm(student.term_grades, subject.id, term2?.id) }}</td>
                    <td v-if="showFinalYear">{{ getFinalGradeForSubject(student.final_grades, subject.id) }}</td>
                </template>
                <td><strong>{{ formatScore(student.overall_average) }}%</strong></td>
                <td>{{ student.promotion_status }}</td>
              </tr>
            </tbody>
          </table>
        </main>

        <!-- التذييل من القالب -->
        <footer v-if="template.footer_content" v-html="processTemplateContent(template.footer_content)" class="report-footer"></footer>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { ElMessage } from 'element-plus';
import { Printer } from '@element-plus/icons-vue';

export default {
name: 'FinalRecordViewer',
components: { Printer },
data() {
  return {
    api: null,
    loading: true,
    classInfo: null,
    subjects: [],
    studentsData: [],
    template: {
      header_content: '',
      footer_content: '',
      layout_options: {}
    },
    terms: [], // لتخزين الفصول الدراسية للسنة
  };
},
computed: {
  showTerm1() { return this.template.layout_options?.show_term1_grades; },
  showTerm2() { return this.template.layout_options?.show_term2_grades; },
  showFinalYear() { return this.template.layout_options?.show_final_year_grade; },
  
  colspanCount() {
      let count = 0;
      if(this.showTerm1) count++;
      if(this.showTerm2) count++;
      if(this.showFinalYear) count++;
      return count > 0 ? count : 1;
  },

  term1() {
      return this.terms.length > 0 ? this.terms[0] : null;
  },
  term2() {
      return this.terms.length > 1 ? this.terms[1] : null;
  }
},
created() {
  this.api = axios.create({ baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api' });
  this.api.interceptors.request.use((config) => {
    const token = localStorage.getItem('access_token');
    if (token) config.headers['Authorization'] = `Bearer ${token}`;
    return config;
  });
  this.fetchData();
},
methods: {
  async fetchData() {
    const { class_id, template_id } = this.$route.query;
    if (!class_id || !template_id) {
      ElMessage.error('المعلومات المطلوبة غير كاملة لعرض السجل.');
      this.loading = false;
      return;
    }
    
    this.loading = true;
    try {
      const [recordDataRes, templateRes] = await Promise.all([
        this.api.get('/final-grade-calculation/final-record', { params: { class_id } }),
        this.api.get(`/report-card-templates/${template_id}`)
      ]);
      
      this.classInfo = recordDataRes.data.class_info;
      this.subjects = recordDataRes.data.subjects;
      this.studentsData = recordDataRes.data.students_data;
      this.template = templateRes.data;

      // بعد جلب البيانات، قم بجلب الفصول الدراسية للسنة الدراسية للطالب
      const studyYearId = this.studentsData[0]?.final_grades[this.subjects[0]?.id]?.study_year_id;
      if (studyYearId) {
          await this.fetchTerms(studyYearId);
      }

    } catch (error) {
      ElMessage.error('فشل في جلب بيانات السجل النهائي.');
      console.error(error);
    } finally {
      this.loading = false;
    }
  },
  async fetchTerms(studyYearId) {
      try {
          const response = await this.api.get(`/study-years/${studyYearId}/terms`);
          this.terms = response.data.sort((a,b) => a.id - b.id);
      } catch (error) {
          console.error("Failed to fetch terms");
      }
  },
  processTemplateContent(content) {
    if (!content) return '';
    const generationDate = new Date().toLocaleDateString('ar-EG');
    return content
      .replace(/{{class_name}}/g, this.classInfo?.name || '')
      .replace(/{{study_year_name}}/g, this.classInfo?.study_year_name || '')
      .replace(/{{generation_date}}/g, generationDate);
  },
  // --- دالة جديدة وآمنة لتنسيق الدرجات ---
  formatScore(score) {
      if (score === null || score === undefined) return '-';
      const numScore = parseFloat(score);
      return isNaN(numScore) ? '-' : numScore.toFixed(2);
  },
  // --- دالة جديدة وآمنة لجلب درجة الفصل الدراسي ---
  getGradeForTerm(termGrades, subjectId, termId) {
      if (!termId) return '-';
      const gradesForSubject = termGrades[subjectId];
      if (!gradesForSubject) return '-';
      
      const grade = gradesForSubject.find(g => g.term_id === termId);
      return this.formatScore(grade?.weighted_average_score_percentage);
  },
  // --- دالة جديدة وآمنة لجلب الدرجة النهائية ---
  getFinalGradeForSubject(finalGrades, subjectId) {
      const grade = finalGrades[subjectId];
      return this.formatScore(grade?.overall_numerical_score_percentage);
  },
  printPage() {
    const printContents = document.getElementById('printable-content').innerHTML;
    const styles = `
      <style>
        body { font-family: sans-serif; }
        @page { size: A4 landscape; margin: 10mm; }
        .report-card-page { width: 100%; page-break-after: always; }
        .final-record-table { width: 100%; border-collapse: collapse; text-align: center; direction: rtl; font-size: 10px; }
        .final-record-table th, .final-record-table td { border: 1px solid #333; padding: 4px; }
        .final-record-table thead th { background-color: #f2f2f2 !important; -webkit-print-color-adjust: exact; font-weight: bold; }
        .report-header, .report-footer { direction: rtl; }
      </style>
    `;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`<html><head><title>السجل النهائي</title>${styles}</head><body>${printContents}</body></html>`);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 500);
  }
},
};
</script>

<style>
/* أنماط للعرض على الشاشة */
.final-record-viewer-wrapper { background-color: #f0f2f5; padding: 20px; }
.print-toolbar { text-align: center; margin-bottom: 20px; }
.report-card-page { background-color: white; width: 297mm; min-height: 210mm; margin: 20px auto; padding: 15mm; box-shadow: 0 0 10px rgba(0,0,0,0.15); box-sizing: border-box; }
.report-header, .report-footer { direction: rtl; text-align: center; }
.final-record-table { width: 100%; border-collapse: collapse; text-align: center; direction: rtl; margin-top: 20px; }
.final-record-table th, .final-record-table td { border: 1px solid #ddd; padding: 8px; font-size: 12px; }
.final-record-table thead th { background-color: #f5f7fa; font-weight: bold; }
</style>
